/**
 * Coin-targeted random news + leak (rumor) pipeline configuration.
 * Generic templates use a {coin} placeholder; coin-specific templates
 * (coinIds set) are flavored parodies matching each coin's personality.
 * Company templates (companyId set) use {company} + {coin} placeholders
 * and only fire for the company's affinity coins (companies.ts).
 */
import { getCompany } from './companies.js';

export type NewsDirection = 1 | -1;

export interface CoinNewsTemplate {
  /** i18n key = 'cnews_' + id */
  id: string;
  direction: NewsDirection;
  /** Momentum push strength in units of coin volatility per tick (decays with remaining time) */
  impact: number;
  /** Effect duration in ms (20~40s per spec) */
  duration: number;
  /** When set, this template only fires for these coins (personality flavor) */
  coinIds?: readonly string[];
  /** When set, this is company news: only fires for the company's affinity coins */
  companyId?: string;
}

export const COIN_NEWS: CoinNewsTemplate[] = [
  // === Generic positive ===
  { id: 'listing', direction: 1, impact: 2.6, duration: 32_000 },
  { id: 'celebrity', direction: 1, impact: 2.2, duration: 24_000 },
  { id: 'partnership', direction: 1, impact: 2.0, duration: 26_000 },
  { id: 'burn', direction: 1, impact: 1.8, duration: 22_000 },
  { id: 'upgrade', direction: 1, impact: 1.6, duration: 24_000 },
  { id: 'etf', direction: 1, impact: 2.4, duration: 38_000 },
  { id: 'payment', direction: 1, impact: 1.7, duration: 22_000 },
  { id: 'viral', direction: 1, impact: 2.0, duration: 20_000 },
  { id: 'airdrop', direction: 1, impact: 1.5, duration: 20_000 },
  { id: 'buyback', direction: 1, impact: 1.9, duration: 24_000 },
  { id: 'influencer_news', direction: 1, impact: 2.1, duration: 22_000 },
  { id: 'patent', direction: 1, impact: 1.4, duration: 20_000 },

  // === Generic negative ===
  { id: 'devs_missing', direction: -1, impact: 2.6, duration: 34_000 },
  { id: 'hack', direction: -1, impact: 2.8, duration: 36_000 },
  { id: 'regulation', direction: -1, impact: 2.0, duration: 26_000 },
  { id: 'delisting', direction: -1, impact: 2.4, duration: 32_000 },
  { id: 'whale_sell', direction: -1, impact: 2.2, duration: 22_000 },
  { id: 'lawsuit', direction: -1, impact: 1.8, duration: 24_000 },
  { id: 'bug', direction: -1, impact: 2.0, duration: 22_000 },
  { id: 'founder_sell', direction: -1, impact: 1.9, duration: 24_000 },
  { id: 'fud', direction: -1, impact: 1.5, duration: 20_000 },
  { id: 'outage', direction: -1, impact: 1.6, duration: 20_000 },

  // === Coin personality flavor ===
  { id: 'stone_difficulty', direction: -1, impact: 1.8, duration: 26_000, coinIds: ['stonecoin'] },
  { id: 'stone_museum', direction: 1, impact: 1.9, duration: 26_000, coinIds: ['stonecoin'] },
  { id: 'nebula_launch', direction: 1, impact: 2.0, duration: 28_000, coinIds: ['nebula'] },
  { id: 'nebula_debris', direction: -1, impact: 1.9, duration: 26_000, coinIds: ['nebula'] },
  { id: 'chicken_payment', direction: 1, impact: 2.4, duration: 30_000, coinIds: ['chickencoin'] },
  { id: 'chicken_flu', direction: -1, impact: 2.5, duration: 28_000, coinIds: ['chickencoin'] },
  { id: 'frog_moon', direction: 1, impact: 2.6, duration: 26_000, coinIds: ['moonfrog'] },
  { id: 'frog_pond', direction: -1, impact: 2.4, duration: 28_000, coinIds: ['moonfrog'] },
  { id: 'pump_festival', direction: 1, impact: 2.5, duration: 28_000, coinIds: ['pumpking'] },
  { id: 'pump_probe', direction: -1, impact: 2.6, duration: 30_000, coinIds: ['pumpking'] },

  // === Company news (Sprint 3a): {company} + {coin}, affinity-targeted ===
  { id: 'co_volt_payment', direction: 1, impact: 2.3, duration: 30_000, companyId: 'voltmotors' },
  { id: 'co_volt_recall', direction: -1, impact: 2.1, duration: 28_000, companyId: 'voltmotors' },
  { id: 'co_moonbase_listing', direction: 1, impact: 2.2, duration: 28_000, companyId: 'moonbase' },
  { id: 'co_moonbase_outage', direction: -1, impact: 2.4, duration: 30_000, companyId: 'moonbase' },
  { id: 'co_hash_rig', direction: 1, impact: 1.8, duration: 24_000, companyId: 'hashworks' },
  { id: 'co_hash_shock', direction: -1, impact: 2.0, duration: 26_000, companyId: 'hashworks' },
  { id: 'co_mle_idol', direction: 1, impact: 2.4, duration: 26_000, companyId: 'moonlightent' },
  { id: 'co_mle_scandal', direction: -1, impact: 2.2, duration: 26_000, companyId: 'moonlightent' },
  { id: 'co_gfry_event', direction: 1, impact: 2.2, duration: 26_000, companyId: 'goldenfry' },
  { id: 'co_gfry_ceo', direction: -1, impact: 2.5, duration: 30_000, companyId: 'goldenfry' },
  { id: 'co_pcap_buy', direction: 1, impact: 2.6, duration: 28_000, companyId: 'pumpcapital' },
  { id: 'co_pcap_probe', direction: -1, impact: 2.5, duration: 30_000, companyId: 'pumpcapital' },
];

export function getNewsTemplate(id: string): CoinNewsTemplate | undefined {
  return COIN_NEWS.find(n => n.id === id);
}

/** Templates eligible for a coin: generic + its personality flavor + affinity company news */
export function templatesForCoin(coinId: string): CoinNewsTemplate[] {
  return COIN_NEWS.filter(n => {
    if (n.companyId) return getCompany(n.companyId)?.affinity.includes(coinId) ?? false;
    return !n.coinIds || n.coinIds.includes(coinId);
  });
}

export const NEWS_CONFIG = {
  /** ms between news publications (45~120s per spec) */
  minInterval: 45_000,
  maxInterval: 120_000,
  /** leak appears this long before the news publishes (30~60s per spec) */
  leakLeadMin: 30_000,
  leakLeadMax: 60_000,
  /** chance a leak precedes the news at all (70% per spec) */
  leakChance: 0.7,
  /** chance the leak points at the real coin + direction (60% genuine / 40% fake) */
  genuineLeakChance: 0.6,
  /** cap on concurrently active news effects */
  maxActiveNews: 2,
  /** feed history keeps this many recent items (news + leaks, saved) */
  feedLimit: 10,
  /** feed panel shows this many recent items */
  feedPanelVisible: 4,
  /** number of leak phrasing variants per direction (i18n leak_up_N / leak_down_N) */
  leakVariants: 3,
  /** first news schedule delay after a fresh state (show the feature early) */
  firstNewsDelayMin: 20_000,
  firstNewsDelayMax: 45_000,
} as const;
